// Global
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import {
  GetStaticComponentProps,
  LinkField,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
// Components
import { getEnum } from 'lib/utils/get-enum';
import { getHeadingLevel } from 'lib/utils/getHeadingLevel';
import { HeadlineWithNavigationTheme } from './HeadlineWithNavigation.theme';
import { IconTypes } from 'src/helpers/SvgIcon/SvgIcon';
import SvgIcon from 'src/helpers/SvgIcon/SvgIcon';
import ImagePrimary from 'src/helpers/Media/ImagePrimary';
import LinkWrapper from 'src/helpers/LinkWrapper/LinkWrapper';
import { Subheadline } from 'src/helpers/Subheadline';
import Headline from 'src/helpers/Headline/Headline';
import { useCurrentScreenType } from 'lib/utils/get-screen-type';
import Component from 'src/helpers/Component/Component';
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { mapSearchResults } from 'lib/graphql/mappers/map-search-results';
import { IntegratedGraphQlResult } from 'lib/graphql/types/integrated-graphql-result';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import { ItemSearchResults } from 'lib/graphql/types/item-search-results';
export type HeadlineWithNavigationLinkProps =
  Feature.EnterpriseWeb.Enterprise.Components.General.HeadlineWithNavigation.HeadlineWithNavigationLink;

export type HeadlineWithNavigationProps =
  Feature.EnterpriseWeb.Enterprise.Components.General.HeadlineWithNavigation.HeadlineWithNavigation & {
    fields: {
      children: [HeadlineWithNavigationLinkProps];
    };
  };

export type ComponentSpacing = '8' | '4';

const HeadlineWithNavigation = (props: HeadlineWithNavigationProps) => {
  const spacing = getEnum<ComponentSpacing>(props.fields?.componentSpacing) || 'standard';
  const spacingValue = spacing === 'standard' ? '8' : '4';
  const context = useSitecoreContext();
  const breadcrumb = (context && context.sitecoreContext?.breadcrumb) || [];
  const { screenType } = useCurrentScreenType();
  const { themeData } = useTheme(HeadlineWithNavigationTheme(spacingValue));
  const noOfTabs = props.fields?.children?.length;
  const headlineCheck = props.fields?.subheadlineText.value;

  return (
    <Component
      dataComponent="general/HeadlineWithNavigation"
      className={breadcrumb?.length === 1 ? 'relative pt-[55px] ml:pt-0' : 'relative'}
      {...props}
    >
      <div className="col-span-12">
        {props?.fields?.headlineText?.value && (
          <div className={themeData.classes.headlineContainer}>
            <div className="col-span-12">
              <Headline
                useTag={getHeadingLevel('h1', props.fields?.headlineLevel)}
                classes={themeData.classes.headline}
                {...props}
              />
            </div>
          </div>
        )}
        <div className={themeData.classes.heroContainer}>
          <ImagePrimary
            imageLayout="fill"
            ratio="hero"
            additionalDesktopClasses="h-[280px] ml:h-[422px]"
            additionalMobileClasses="h-[280px]"
            priority
            {...props}
          ></ImagePrimary>
          <div className={themeData.classes.linkContainer}>
            <Subheadline classes={themeData.classes.subheadlineStyle} {...props} />
            {props.fields?.children?.map((_item: HeadlineWithNavigationLinkProps, i: number) => {
              const _linkField = _item.fields?.navigationLink as LinkField;
              const _icon = getEnum<IconTypes>(_item.fields?.navigationIcon);

              return (
                <div key={i} className={themeData.classes.linkStyle}>
                  <LinkWrapper
                    className={`${themeData.classes.linkWrapperStyle} ${
                      (noOfTabs > 3 && headlineCheck != '' && screenType === 'mml') ||
                      screenType === 'ml'
                        ? 'text-xs'
                        : 'text-s'
                    }`}
                    field={_linkField}
                    ariaLabel={{
                      value: (_item.fields?.navigationIcon?.name as string) || 'Navigation Icon',
                    }}
                  >
                    {_item.fields?.navigationIcon && (
                      <div className={themeData.classes.svgWrapper}>
                        <SvgIcon icon={_icon} className={themeData.classes.svgIconStyle} />
                      </div>
                    )}
                  </LinkWrapper>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Component>
  );
};
type IntegratedGraphQl = IntegratedGraphQlResult<{
  item: {
    fields: ItemFieldResult[];
    children: ItemSearchResults<{
      template: { name: string };
      fields: ItemFieldResult[];
    }>;
  };
}>;

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  if (rendering.fields === undefined || !('data' in rendering.fields)) {
    return {};
  }

  const fields = rendering.fields as unknown as IntegratedGraphQl;

  const result = {
    fields: {
      ...mapItemFieldResultsToObject(fields.data.item.fields),
      children: mapSearchResults(fields.data.item.children, (child) => ({
        fields: {
          ...mapItemFieldResultsToObject(child.fields),
        },
      })),
    },
  };
  return result;
};
export default withDatasourceCheck()<HeadlineWithNavigationProps>(HeadlineWithNavigation);
